package fr.frux.core.designsystem.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf

/**
 * Registre des surcouches qui consomment un geste de retour avant la pile de navigation.
 *
 * Nécessaire sur web : le back du navigateur n'atteint que l'hôte (`window.history`), qui ne voit
 * pas les bottom sheets — l'hôte consulte ce registre avant de popper la pile. Sur Android, les
 * `ModalBottomSheet` interceptent déjà le retour système via leur fenêtre de dialogue. Le
 * registre est consulté par le `onBack` de `NavDisplay` (`handleNavBack`) pour les écrans qui
 * interceptent sans fenêtre propre, comme l'assistant d'ajout d'arbre.
 */
@Stable
class NavigationBackInterceptors {
    private val handlers = mutableListOf<() -> Unit>()
    private var count by mutableIntStateOf(0)

    /** Observable : vrai tant qu'une surcouche ou un écran intercepte le retour. */
    val hasHandlers: Boolean get() = count > 0

    /** Enregistre [handler] et retourne sa fonction de désenregistrement. */
    fun register(handler: () -> Unit): () -> Unit {
        handlers.add(handler)
        count = handlers.size
        return {
            handlers.remove(handler)
            count = handlers.size
        }
    }

    /**
     * Invoque l'intercepteur le plus récemment enregistré (la surcouche la plus haute).
     * Retourne false si aucune surcouche n'est ouverte : le geste revient alors à la pile.
     */
    fun dispatchBack(): Boolean {
        val handler = handlers.lastOrNull() ?: return false
        handler()
        return true
    }
}

/** Fourni par la racine de l'app ; null hors de ce cadre (previews, tests d'écran isolés). */
val LocalNavigationBackInterceptors = staticCompositionLocalOf<NavigationBackInterceptors?> { null }

/**
 * Déclare que la surcouche courante consomme le prochain geste de retour ([onBack], typiquement
 * la fermeture d'une feuille). Actif tant que le composable est en composition.
 */
@Composable
fun InterceptNavigationBack(onBack: () -> Unit) {
    val interceptors = LocalNavigationBackInterceptors.current ?: return
    val currentOnBack by rememberUpdatedState(onBack)
    DisposableEffect(interceptors) {
        val unregister = interceptors.register { currentOnBack() }
        onDispose { unregister() }
    }
}
